import { axialHexDistance } from '../hexmap/coords';
import { getHexTileMapKey } from '../hexmap/model/keys';
import { getTile, hexesWithinRadius } from '../hexmap/model/map';
import type { HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import { milliCost } from './fixed';
import { buildingIndex } from './footprint';
import { buildingLevel, levelDef, upgradeCityLevel } from './levels';
import { canAfford } from './stock';
import { isCity, mintOrder, territoryOwners } from './territory';
import type {
  Building,
  BuildingType,
  Cell,
  EntityId,
  PlayerId,
  SimContext,
  SimState,
} from './types';
import { watchStats } from './vision';

/**
 * Shared yes/no checks with reasons for building actions: `canPlace`,
 * `canFoundCity`, `canUpgrade`, `canCancel`, `canWatch`. The sim's command
 * handlers and the UI's disabled buttons both use them (the UI passing
 * fog-safe lookups), so a refusal reads the same in both places.
 */

type PlaceRejection = 'unknown' | 'tile-type' | 'territory' | 'era' | 'occupied' | 'limit' | 'cost';

type PlaceCheck = { ok: true } | { ok: false; reason: PlaceRejection };

/**
 * How many `type` buildings of `playerId` (sites too) stand in city
 * `cityId`'s territory, by their main tile: what `maxPerCity` limits.
 * `owners` is `territoryOwners`.
 */
function buildingCountIn(
  state: SimState,
  owners: ReadonlyMap<HexTileMapKey, EntityId>,
  playerId: PlayerId,
  type: BuildingType,
  cityId: EntityId
): number {
  let count = 0;
  for (const b of Object.values(state.buildings)) {
    if (b.owner !== playerId || b.type !== type) continue;
    if (owners.get(getHexTileMapKey(b.pos)) === cityId) count++;
  }
  return count;
}

/**
 * Least distance between cities: the city type's `minDistanceToCity` (rules
 * default it), or 0 when the rules have no city.
 */
function minCityDistance(rules: RuleSet): number {
  return rules.buildings.city?.minDistanceToCity ?? 0;
}

/**
 * Whether `playerId` may place a `type` site on `cell` right now. Used by the
 * `place` command and by the UI preview; the UI passes `occupied` built from
 * what the player knows about the tile, so the preview never reveals
 * buildings hidden by fog (the sim then has the final say).
 */
function canPlace(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  type: BuildingType,
  cell: Cell,
  occupied?: (key: HexTileMapKey) => boolean
): PlaceCheck {
  const def = ctx.rules.buildings[type];
  const player = state.players[playerId];
  const tile = getTile(ctx.terrain, cell);
  // Settler-founded buildings (cities) aren't placed.
  if (!def?.placement || def.consumesBuilder || !player || player.eliminated || !tile) {
    return { ok: false, reason: 'unknown' };
  }
  if (!def.placement.on.includes(tile.type)) return { ok: false, reason: 'tile-type' };

  const key = getHexTileMapKey(cell);
  const owners = territoryOwners(state, ctx.rules);
  const cityId = owners.get(key);
  if (
    def.placement.within === 'territory' &&
    (cityId === undefined || state.buildings[cityId]?.owner !== playerId)
  ) {
    return { ok: false, reason: 'territory' };
  }
  // An era unlock: the tile's city must have reached `placement.cityLevel`.
  const needed = def.placement.cityLevel;
  if (needed !== undefined) {
    const city = cityId === undefined ? undefined : state.buildings[cityId];
    if (!city || buildingLevel(city) < needed) return { ok: false, reason: 'era' };
  }

  // Any tile of a building's footprint is taken.
  const taken = occupied ? occupied(key) : buildingIndex(state).has(key);
  if (taken) return { ok: false, reason: 'occupied' };

  if (
    def.maxPerCity !== undefined &&
    cityId !== undefined &&
    buildingCountIn(state, owners, playerId, type, cityId) >= def.maxPerCity
  ) {
    return { ok: false, reason: 'limit' };
  }

  if (!canAfford(state, playerId, milliCost(def.cost ?? {}))) return { ok: false, reason: 'cost' };
  return { ok: true };
}

/** Ticks (0.1 s each: 3 s) after taking damage during which a building can't be upgraded. */
const UNDER_ATTACK_TICKS = 30;

type UpgradeRejection =
  | 'unknown'
  | 'max-level'
  | 'upgrading'
  | 'under-attack'
  | 'city-level'
  | 'cost';

type UpgradeCheck =
  | { ok: true; level: number }
  | { ok: false; reason: Exclude<UpgradeRejection, 'city-level'> }
  /** `cityLevel`: the level the building's city needs (see levels.ts `upgradeCityLevel`). */
  | { ok: false; reason: 'city-level'; cityLevel: number };

/**
 * Whether `playerId` may raise building `buildingId` one level now. It must be
 * their own completed building with a next level they can afford. A placed
 * building (farm, forestry) can't pass the level of the city whose territory
 * it stands in, nor reach a level whose `cityLevel` that city is below. One
 * already upgrading can't start another ('upgrading'). Nor
 * may one that took damage in the last UNDER_ATTACK_TICKS be upgraded
 * ('under-attack'): money spent on a building about to fall is wasted.
 * `owners` takes a precomputed `territoryOwners` (a caller checking many).
 */
function canUpgrade(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  buildingId: EntityId,
  owners?: ReadonlyMap<HexTileMapKey, EntityId>
): UpgradeCheck {
  const b = state.buildings[buildingId];
  const def = b && ctx.rules.buildings[b.type];
  const player = state.players[playerId];
  if (!b || !def || b.owner !== playerId || b.buildProgress < 1 || !player || player.eliminated) {
    return { ok: false, reason: 'unknown' };
  }
  const level = buildingLevel(b) + 1;
  const next = levelDef(def, level);
  if (!next) return { ok: false, reason: 'max-level' };
  if (b.upgrading) return { ok: false, reason: 'upgrading' };
  if (b.lastDamagedTick !== undefined && state.tick - b.lastDamagedTick < UNDER_ATTACK_TICKS) {
    return { ok: false, reason: 'under-attack' };
  }

  const cityLevel = upgradeCityLevel(def, level);
  if (cityLevel > 0) {
    const city =
      state.buildings[
        (owners ?? territoryOwners(state, ctx.rules)).get(getHexTileMapKey(b.pos)) ?? ''
      ];
    if (!city || city.owner !== playerId || buildingLevel(city) < cityLevel) {
      return { ok: false, reason: 'city-level', cityLevel };
    }
  }

  if (!canAfford(state, playerId, milliCost(next.cost))) return { ok: false, reason: 'cost' };
  return { ok: true, level };
}

type FoundCityRejection = 'unknown' | 'occupied' | 'too-close';

type FoundCityCheck =
  | { ok: true }
  | { ok: false; reason: Exclude<FoundCityRejection, 'too-close'> }
  /** `cityId`: the nearest city in the way (lowest id on a tie), `distance` tiles away. */
  | { ok: false; reason: 'too-close'; cityId: EntityId; distance: number };

/**
 * Whether `playerId`'s unit `unitId` may found a city where it stands: it must
 * be their unit with the `found-city` action, on a tile with no building, and
 * no city (any owner, sites included) may be closer than the city's
 * `minDistanceToCity` (exactly that distance is allowed). Used by the
 * `foundCity` command and the Found City button; the UI passes `buildingAt`
 * built from what the player knows, so the reason never reveals a city
 * hidden by fog (the sim then has the final say). `at` checks another tile
 * instead of where the unit stands (the UI's hint for the tile under the
 * cursor while the settler walks).
 */
function canFoundCity(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitId: EntityId,
  buildingAt?: (key: HexTileMapKey) => Building | null,
  at?: Cell
): FoundCityCheck {
  const unit = state.units[unitId];
  const def = ctx.rules.buildings.city;
  if (
    !unit ||
    unit.owner !== playerId ||
    !def ||
    !ctx.rules.units[unit.type]?.actions.includes('found-city')
  ) {
    return { ok: false, reason: 'unknown' };
  }
  let lookup = buildingAt;
  if (!lookup) {
    const index = buildingIndex(state);
    lookup = (key) => index.get(key) ?? null;
  }
  const pos = at ?? unit.pos;
  if (lookup(getHexTileMapKey(pos))) return { ok: false, reason: 'occupied' };
  const minDist = def.minDistanceToCity;
  let nearest: { cityId: EntityId; distance: number } | null = null;
  for (const cell of hexesWithinRadius(pos, minDist - 1)) {
    const b = lookup(getHexTileMapKey(cell));
    if (!b || !isCity(b, ctx.rules)) continue;
    const distance = axialHexDistance(pos, b.pos);
    if (
      !nearest ||
      distance < nearest.distance ||
      (distance === nearest.distance && mintOrder(b.id) < mintOrder(nearest.cityId))
    ) {
      nearest = { cityId: b.id, distance };
    }
  }
  return nearest ? { ok: false, reason: 'too-close', ...nearest } : { ok: true };
}

type WatchRejection = 'unknown' | 'not-ready' | 'range';

type WatchCheck =
  | { ok: true }
  | { ok: false; reason: Exclude<WatchRejection, 'range'> }
  /** `range`: the tower's current watch reach, which the tile is beyond. */
  | { ok: false; reason: 'range'; range: number };

/**
 * Whether `playerId` may point building `buildingId` at `target` (the
 * `watch` command): their own building with a watch (rules `watchRange`),
 * completed, and the tile on the map within its current `watchRange` hexes
 * of it. Any such tile will do, explored or not: watching is how a player
 * looks into the fog. A `null` target (stop watching) only needs the first
 * two.
 */
function canWatch(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  buildingId: EntityId,
  target: Cell | null
): WatchCheck {
  const b = state.buildings[buildingId];
  const stats = b ? watchStats(ctx.rules, b) : undefined;
  if (!b || b.owner !== playerId || !stats) return { ok: false, reason: 'unknown' };
  if (b.buildProgress < 1) return { ok: false, reason: 'not-ready' };
  if (target === null) return { ok: true };
  if (!getTile(ctx.terrain, target)) return { ok: false, reason: 'unknown' };
  if (axialHexDistance(b.pos, target) > stats.range) {
    return { ok: false, reason: 'range', range: stats.range };
  }
  return { ok: true };
}

export type {
  FoundCityCheck,
  FoundCityRejection,
  PlaceCheck,
  PlaceRejection,
  UpgradeCheck,
  UpgradeRejection,
  WatchCheck,
  WatchRejection,
};

/**
 * Whether `playerId` may cancel building `buildingId`: their own unfinished
 * site of a placed building (a city site is founded, not placed).
 */
function canCancel(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  buildingId: EntityId
): boolean {
  const b = state.buildings[buildingId];
  return !!b && b.owner === playerId && b.buildProgress < 1 && !!rules.buildings[b.type]?.placement;
}

/** What cancelling a `type` site gives back: `pacing.cancelRefund` of its cost. */
function cancelRefund(rules: RuleSet, type: BuildingType): Record<string, number> {
  const refund: Record<string, number> = {};
  for (const [res, amount] of Object.entries(rules.buildings[type]?.cost ?? {})) {
    refund[res] = amount * rules.pacing.cancelRefund;
  }
  return refund;
}

export {
  buildingCountIn,
  canCancel,
  cancelRefund,
  canFoundCity,
  canPlace,
  canUpgrade,
  canWatch,
  minCityDistance,
  UNDER_ATTACK_TICKS,
};
